// File: components/navigation/Sidebar.js

import React from 'react';
import { Folder, ChevronDown, Loader2, AlertTriangle } from 'lucide-react';
import ViewToggle from './ViewToggle.js';
import { loadRegulationIndex } from '../../utils/dataLoader.js';
import { getSettings } from '../../utils/storage.js';
import { NAVIGATION_VIEWS } from '../../utils/constants.js';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * Sidebar Component
 * 
 * Navigation sidebar with:
 * - View toggle (Topic/Code)
 * - Collapsible subpart sections (accordion mode)
 * - Section navigation
 * 
 * Mobile-optimized: Full-screen overlay on mobile, side panel on desktop
 * Accordion behavior: Only one subpart expanded at a time
 */
const Sidebar = ({ 
    regulationCode = '1910',
    currentSection = null,
    onSectionClick = null,
    isOpen = false,
    onClose = null,
    className = ''
}) => {
    const { lang, t } = useLanguage();
    const [viewMode, setViewMode] = React.useState(NAVIGATION_VIEWS.TOPIC);
    const [subparts, setSubparts] = React.useState([]);
    const [topics, setTopics] = React.useState([]);
    const [expandedSubpart, setExpandedSubpart] = React.useState(null);
    const [expandedTopic, setExpandedTopic] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    // Decode HTML entities
    const decodeEntities = (text) => {
        if (!text) return '';
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        return textarea.value;
    };

    // Load regulation index and topics on mount, or when the regulation or
    // interface language changes (localized files fall back to English).
    React.useEffect(() => {
        // Load topics (topics.es.json / topics.ar.json when localized)
        const loadTopics = async () => {
            try {
                let response = lang !== 'en'
                    ? await fetch(`/data/topics.${lang}.json`)
                    : await fetch('/data/topics.json');
                if (!response.ok && lang !== 'en') {
                    response = await fetch('/data/topics.json');
                }
                if (response.ok) {
                    const data = await response.json();
                    // Filter topics to only include sections for current regulation
                    const filteredTopics = data.topics
                        .map(topic => ({
                            ...topic,
                            sections: topic.sections.filter(s => s.startsWith(regulationCode))
                        }))
                        .filter(topic => topic.sections.length > 0)
                        .sort((a, b) => a.name.localeCompare(b.name, lang));
                    setTopics(filteredTopics);
                }
            } catch (err) {
                console.error('Failed to load topics:', err);
            }
        };

        // Load regulation structure
        const loadRegulationData = async () => {
            setLoading(true);
            setError(null);

            try {
                const result = await loadRegulationIndex(regulationCode, lang);

                if (result.error) {
                    setError(result.error);
                    setSubparts([]);
                } else {
                    setSubparts(result.subparts || []);
                }
            } catch (err) {
                setError(t('sidebar.loadError'));
                setSubparts([]);
            } finally {
                setLoading(false);
            }
        };

        loadRegulationData();
        loadTopics();
    }, [regulationCode, lang, t]);

    // Load saved view preference
    React.useEffect(() => {
        const settings = getSettings();
        setViewMode(settings.navigationView || NAVIGATION_VIEWS.TOPIC);
    }, []);

    // Listen for toggle sidebar event from TopBar
    React.useEffect(() => {
        const handleToggle = () => {
            if (onClose) {
                onClose();
            }
        };

        document.addEventListener('toggle-sidebar', handleToggle);
        return () => document.removeEventListener('toggle-sidebar', handleToggle);
    }, [onClose]);

    // Handle view mode change
    const handleViewChange = (newView) => {
        setViewMode(newView);
        // Collapse all sections when switching views
        setExpandedSubpart(null);
    };

    // Toggle subpart expansion (accordion mode)
    const toggleSubpart = (subpartLetter) => {
        if (expandedSubpart === subpartLetter) {
            // Collapse if already expanded
            setExpandedSubpart(null);
        } else {
            // Expand new section (auto-collapses others)
            setExpandedSubpart(subpartLetter);
        }
    };

    // Handle section click
    const handleSectionClick = (section) => {
        if (onSectionClick) {
            onSectionClick(section);
        }

        // Close sidebar on mobile after selecting a section
        if (window.innerWidth < 768 && onClose) {
            onClose();
        }
    };

    // Toggle topic expansion
    const toggleTopic = (topicName) => {
        if (expandedTopic === topicName) {
            setExpandedTopic(null);
        } else {
            setExpandedTopic(topicName);
        }
    };

    // Render topic view (grouped by topics like Ladders, Scaffolding, etc.)
    const renderTopicView = () => {
        if (topics.length === 0) {
            return <div className="sidebar-empty">{t('sidebar.noTopics')}</div>;
        }

        return topics.map(topic => {
            const isExpanded = expandedTopic === topic.name;

            return (
                <div key={topic.name} className="sidebar-topic">
                    <div
                        className={`sidebar-item topic-header ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => toggleTopic(topic.name)}
                    >
                        <Folder size={16} />
                        <span className="topic-name">{topic.name}</span>
                        <span className="topic-count">({topic.sections.length})</span>
                        <ChevronDown size={16} className={`expand-icon ${isExpanded ? 'rotated' : ''}`} />
                    </div>

                    {/* Expandable sections list */}
                    <div className={`topic-content ${isExpanded ? 'expanded' : ''}`}>
                        {topic.sections.map(sectionCode => (
                            <div
                                key={sectionCode}
                                className={`sidebar-item sub-topic ${
                                    currentSection === sectionCode ? 'active' : ''
                                }`}
                                onClick={() => handleSectionClick({ code: sectionCode, title: '' })}
                            >
                                <span className="code-number">{sectionCode}</span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        });
    };

    // Render code view (collapsible sections with accordion)
    const renderCodeView = () => {
        return subparts.map(subpart => {
            const isExpanded = expandedSubpart === subpart.letter;
            const sections = subpart.sections || [];

            return (
                <div key={subpart.letter} className="sidebar-section">
                    {/* Collapsible Header */}
                    <div 
                        className={`sidebar-section-header ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => toggleSubpart(subpart.letter)}
                    >
                        <div className="subpart-title">
                            <span className="subpart-letter">{subpart.letter}</span>
                            <span className="subpart-name-short">{decodeEntities(subpart.name)}</span>
                        </div>
                        <span className="subpart-range">
                            ({sections.length > 0
                                ? `${sections[0].code.split('.')[1]}-${sections[sections.length - 1].code.split('.')[1]}`
                                : '0'})
                        </span>
                        <ChevronDown size={16} className={`expand-icon ${isExpanded ? 'rotated' : ''}`} />
                    </div>

                    {/* Collapsible Content */}
                    <div className={`subpart-content ${isExpanded ? 'expanded' : ''}`}>
                        {sections.map(section => (
                            <div 
                                key={section.code}
                                className={`sidebar-item sub-code ${
                                    currentSection === section.code ? 'active' : ''
                                }`}
                                onClick={() => handleSectionClick(section)}
                            >
                                <span className="code-number">{section.code}</span>
                                <span className="code-title">{decodeEntities(section.title)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        });
    };

    return (
        <>
            {/* Sidebar (overlay + slide positioning handled by MainLayout) */}
            <aside className={`sidebar ${isOpen ? 'open' : ''} ${className}`}>
                {/* View Toggle */}
                <ViewToggle onViewChange={handleViewChange} />

                {/* Content (mirrors to RTL for Arabic; codes stay LTR digits) */}
                <div className="sidebar-content" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                    {loading && (
                        <div className="sidebar-loading">
                            <Loader2 size={20} className="animate-spin" />
                            <p>{t('sidebar.loading')}</p>
                        </div>
                    )}

                    {error && (
                        <div className="sidebar-error">
                            <AlertTriangle size={20} />
                            <p>{error}</p>
                        </div>
                    )}

                    {!loading && !error && (
                        <>
                            {viewMode === NAVIGATION_VIEWS.TOPIC 
                                ? renderTopicView() 
                                : renderCodeView()
                            }
                        </>
                    )}
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
